import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Icon } from '../../ui/Icon'
import { Thumb } from '../../ui/Thumb'
import { Failed } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { Pagination } from '../../ui/Pagination'
import { useProducts } from '../products/api'
import { priceLabel } from '../products/model'
import type { Category } from './api'

/** Строк на странице: по умолчанию десять, больше — по выбору. */
export const PAGE_SIZES = [10, 25, 50] as const

export interface CategoryProductsProps {
  category: Category
}

/**
 * Вкладка «Товары»: состав категории страницами по десять строк, как в
 * каталоге. Листает сервер — экран передаёт только `page` и `page_size`.
 */
export function CategoryProducts({ category }: CategoryProductsProps) {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<number>(PAGE_SIZES[0])

  const products = useProducts({ categoryId: category.id, page, pageSize: size })

  const list = products.data?.items ?? []

  return (
    <section className="ui-table-card" aria-labelledby="c-prods">
      <div className="ui-card-head">
        <h3 id="c-prods">Товары категории</h3>
      </div>
      {products.isPending ? <TableSkeleton what="товары" /> : null}
      {products.isError ? (
        <Failed
          what="товары"
          error={products.error}
          onRetry={() => {
            void products.refetch()
          }}
        />
      ) : null}
      {products.data ? (
        list.length === 0 ? (
          <div className="ui-empty">
            <Icon name="i-pill" />
            <b>В категории пока нет товаров</b>
          </div>
        ) : (
          <>
            <div className="ui-table-wrap" aria-busy={products.isPlaceholderData}>
              <table className="ui-table ui-products">
                <colgroup>
                  <col />
                  <col className="w-price" />
                  <col className="w-stock" />
                  <col className="w-cb" />
                  <col className="w-on" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Товар</th>
                    <th scope="col" data-align="end">
                      Цена
                    </th>
                    <th scope="col">Наличие</th>
                    <th scope="col" data-align="end">
                      Кэшбэк
                    </th>
                    <th scope="col" data-align="center">
                      В приложении
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((product) => {
                    const href = `/products/${product.id}`
                    return (
                      <tr
                        key={product.id}
                        data-href={href}
                        onClick={() => {
                          void navigate(href)
                        }}
                      >
                        <td>
                          <div className="ui-product">
                            <Thumb photos={product.photos} size={40} alt={product.name} />
                            <div className="txt">
                              <Link
                                to={href}
                                className="primary"
                                onClick={(event) => {
                                  event.stopPropagation()
                                }}
                              >
                                {product.name}
                              </Link>
                              <span className="meta">
                                <span className="maker">{product.maker}</span>
                                {product.rx ? (
                                  <span className="ui-tag" data-tone="rx">
                                    рецепт
                                  </span>
                                ) : null}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="num" data-align="end">
                          {priceLabel(product)}
                        </td>
                        <td>
                          {product.inStock ? (
                            <span className="ui-status" data-tone="success">
                              есть
                            </span>
                          ) : (
                            <span className="ui-status">нет</span>
                          )}
                        </td>
                        {/* Процент категории — приглушён, свой — выделен: глаз
                            цепляется только за исключения. */}
                        <td data-align="end">
                          {product.cbOwn === null ? (
                            <span className="cb-inherited">
                              {product.cb}%<span className="visually-hidden"> из категории</span>
                            </span>
                          ) : (
                            <span className="cb-own">
                              <span className="ui-tag">свой</span>
                              {product.cb}%
                            </span>
                          )}
                        </td>
                        <td data-align="center">
                          {product.active ? (
                            <span className="ui-status" data-tone="success">
                              показан
                            </span>
                          ) : (
                            <span className="ui-status">скрыт</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              label="Страницы товаров категории"
              page={page}
              pageSize={size}
              total={products.data.total}
              sizes={PAGE_SIZES}
              busy={products.isPlaceholderData}
              onPage={setPage}
              onPageSize={(next) => {
                setSize(next)
                setPage(1)
              }}
            />
          </>
        )
      ) : null}
    </section>
  )
}
